import { KeyboardArrowDownIcon } from '@assets/icons';
import {
  FormHelperText,
  FormLabel,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import './Select.scss';

const AppSelect: FC<AppSelectProps> = ({
  id,
  label,
  required,
  disabled,
  translated,
  placeholder,
  options = [],
  valueType = 'string',
  value,
  defaultValue,
  isRow = false,
  onChange,
  error,
}) => {
  const { t } = useTranslation();

  const errorText = useMemo(() => {
    if (error?.type === 'required' && !error.message) {
      return t('common.please_select', { 0: t(label!) });
    }

    return error?.message ? t(error?.message) : null;
  }, [error]);

  const handleChange = useCallback(
    (event: SelectChangeEvent<string | number>) => {
      if (valueType === 'number') {
        event.target.value = Number(event.target.value);
      }
      onChange?.(event);
    },
    [onChange]
  );

  const renderValue = useCallback(
    (selected: string) => {
      if (selected) {
        const label = options.find(opt => opt.value === selected)?.label ?? '';
        return translated ? label : t(label);
      }
      return (
        <Typography sx={{ fontSize: '16px' }}>{t(placeholder!)}</Typography>
      );
    },
    [options]
  );

  return (
    <Grid
      container
      className='Select'
      direction={isRow ? 'row' : 'column'}
      spacing={isRow ? 3 : 2}
    >
      {label && (
        <Grid
          item
          xs={4}
          alignItems='center'
          justifyContent={isRow ? 'flex-end' : 'flex-start'}
          textAlign={isRow ? 'right' : undefined}
        >
          <FormLabel htmlFor={id} required={required}>
            {t(label)}
          </FormLabel>
        </Grid>
      )}
      <Grid item xs={8} sx={{ width: '100%', flexDirection: 'column' }}>
        <Select
          id={id}
          name={id}
          value={value != null ? String(value) : undefined}
          defaultValue={defaultValue != null ? String(defaultValue) : undefined}
          displayEmpty
          disabled={disabled}
          IconComponent={KeyboardArrowDownIcon}
          renderValue={renderValue}
          onChange={handleChange}
          error={!!error?.type}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 300,
                overflowY: 'auto',
              },
            },
          }}
        >
          {options.map(({ value, label, disabled }) => (
            <MenuItem key={value} value={value} disabled={disabled}>
              {translated ? label : t(label)}
            </MenuItem>
          ))}
        </Select>
        {errorText && <FormHelperText error>{errorText}</FormHelperText>}
      </Grid>
    </Grid>
  );
};

export default AppSelect;
